// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// OS VERBOS DA SELECÇÃO — o que o `Ctrl+K` passa a oferecer, e porque é que o resto não dá
// ============================================================================
//
// 📗 §12.3 do desenho (`docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`),
//    a peça **H**: *«a resposta não é inventar dez atalhos. É um só, que já existe e que já se
//    aprende: `Ctrl`+`K` passa a conhecer a selecção»*. E a propriedade que o justifica está na
//    mesma secção: *«não há becos — um verbo que não se aplica **aparece e diz porquê**»*.
//
// ⚠️⚠️ **A REGRA QUE ISTO EXISTE PARA FECHAR, e não é «faltam atalhos»:**
//
//    > **Todo o verbo que a barra de acções oferece sobre a selecção tem de existir na paleta —
//    > e quando não se aplica, tem de dizer porquê e o que o desbloquearia.**
//
//    📌 **Medido a 2026-09-15**, contando por CHAMADA e não por ficheiro (regra 8, ponto 2): a
//    barra tem **22 verbos** (21 sobre a frota, 3 sobre um serviço subcontratado, com
//    `cancelarServico` e `retirarDoMapa` a servir os dois). ⚠️ **20 → 22 com o par da cópia**
//    (peça F §6.5). Num **dia livre** aplicam-se **3**; os outros **19 estão simplesmente
//    AUSENTES da barra, sem explicação em lado nenhum**.
//    Hoje, a pergunta *«porque é que não posso partir esta viagem?»* não tem resposta em
//    sítio nenhum do sistema — e é esta tabela que lha dá.
//
// ⚠️ **ISTO NÃO DECIDE NADA SOBRE O ECRÃ.** Não sabe o que cada verbo faz (o `fazer` vem do
//    `Mapa.tsx`, e é **a mesma função** que a barra recebe — duas construções do mesmo gesto
//    divergiriam, e a que divergisse era a que ninguém corre). Aqui só se responde a duas
//    perguntas: *que verbos existem* e *este aplica-se a esta selecção*.
//
// ⚠️ **A guarda está em `paleta-conhece-a-seleccao.test.tsx`**, em duas metades que se não
//    substituem: uma varre o TEXTO do `Mapa.tsx` e exige que todo o `onClick={…}` da barra
//    tenha entrada aqui; a outra RENDERIZA o mapa em cada contexto e exige que o conjunto de
//    botões da barra seja exactamente o conjunto de verbos aplicáveis desta tabela. Sem a
//    segunda, os nomes batiam e os predicados podiam divergir em silêncio.
// ============================================================================

/** O que a célula seleccionada é — a mesma classificação que o painel de detalhe usa. */
export type NaturezaDaCelula = 'servico' | 'livre' | 'indisponivel';

/**
 * Os factos sobre a selecção de que os predicados precisam — e **só** esses.
 *
 * ⚠️ Cada campo aqui é um facto que a barra de acções já testa no seu JSX. Acrescentar um
 * campo que a barra não testa é acrescentar uma segunda verdade sobre a mesma pergunta.
 */
export interface ContextoDaSeleccao {
  /** `nada` = ninguém escolheu célula nenhuma; a paleta fica global, como sempre foi. */
  readonly onde: 'nada' | 'frota' | 'subcontratado';
  /** Só existe na frota. Num subcontratado a célula é de outra empresa (D-002). */
  readonly natureza?: NaturezaDaCelula | undefined;
  /** O dia já passou — é o que separa «fechar um serviço» de inventar km reais (D-091). */
  readonly jaPassou: boolean;
  /** A célula tem estado de viatura (oficina, avaria, sinistro) — `celula[7] >= 0`. */
  readonly indisponivel: boolean;
  /** Dia de rotação: há uma SEGUNDA viagem que parte neste dia (migração 027, campo 13). */
  readonly temViagemQueParte: boolean;
  /** A viagem já tem alguma fronteira de troço marcada — sem ela não há o que juntar. */
  readonly temFronteiraDeTroco: boolean;
  /** A célula tem referência de corrida — sem ela não há viagem a que dar cor. */
  readonly temCorrida: boolean;
  /**
   * 🔴 **A MESMA viagem corre noutro autocarro** (peça F §6.5) — `outrasViaturasDaViagem`.
   *
   * ⚠️ É a condição de «Tirar este autocarro da viagem…», e é o espelho exacto da recusa do
   * servidor: sem mais nenhum executor, o que se apagaria **não é uma cópia, é a viagem**, e a
   * escrita responde 409 `unica_viatura_com_a_viagem`. 📌 *«Um botão presente que só serve para
   * recusar é ruído»* — a mesma razão do `temFronteiraDeTroco`.
   */
  readonly temOutraViatura: boolean;
  /** Dia sem itinerário: é aí, e só aí, que se diz onde a viatura está parada. */
  readonly diaSemItinerario: boolean;
  /** Há alvo de diário — exige serviço **e** viatura identificada. */
  readonly temDiario: boolean;
}

/**
 * ⚠️⚠️ **O nome de cada verbo é o nome da PROPRIEDADE que a barra de acções recebe.**
 *
 * 📌 **E é uma união e não `string` de propósito — é a terceira guarda, e é de compilação:** o
 * `Mapa.tsx` declara o seu `gestos` como `Record<ChaveDoVerbo, (() => void) | undefined>`, logo
 * um verbo declarado aqui sem gesto lá, ou um gesto lá sem verbo aqui, **não compila**. As
 * outras duas guardas (a varredura do texto e a comparação com o DOM) vivem em
 * `paleta-conhece-a-seleccao.test.tsx` e apanham coisas diferentes: esta prova que os nomes
 * existem nos dois lados, aquelas provam que os predicados concordam.
 */
export type ChaveDoVerbo =
  | 'substituir'
  | 'editar'
  // 🔴 Peça F §6.5 — a mesma viagem passa a correr TAMBÉM num segundo autocarro, e o inverso
  //    que o tira de lá. ⚠️ **Os dois vivem na barra**, ao contrário do par da espera: as duas
  //    pontas deste gesto agem sobre uma célula da frota que existe (a do autocarro que tem a
  //    viagem, e a do que a vai deixar de ter), logo há selecção que as nomeie.
  | 'copiarPeriodo'
  | 'retirarCopia'
  | 'fecharServico'
  | 'retirarDoMapa'
  | 'cancelarServico'
  | 'travar'
  | 'diario'
  | 'editarQuePartem'
  | 'corDaViagemQuePartem'
  | 'corrigir'
  | 'anotar'
  | 'partir'
  | 'organizarEmTrocos'
  | 'juntarTrocos'
  // 🔴 Peça F §6.3 — a viagem sai do autocarro e fica à espera de outro.
  // ⚠️ **Não há `trazerDeEspera` aqui, e é do domínio e não esquecimento:** o inverso age sobre
  //    uma viagem que não está em viatura nenhuma, logo não existe selecção da frota que o
  //    nomeie. Ele vive na barra da secção «Em espera» — ver `aoClicar` em `Mapa.tsx`.
  | 'porEmEspera'
  | 'corDaViagem'
  | 'onde'
  | 'marcarIndisponivel'
  | 'encaixar'
  | 'trocarEmpresa';

/** Os âmbitos da barra de acções, pela ordem por que ela os desenha. */
export type GrupoDoVerbo =
  | 'urgência'
  | 'a viagem'
  | 'a viagem que parte'
  | 'este dia'
  | 'a viatura'
  | 'o serviço subcontratado';

export interface VerboDaSeleccao {
  /**
   * ⚠️⚠️ **A chave é o NOME DA PROPRIEDADE que a barra recebe** (`editar`, `anotar`, …), e não
   * um nome bonito. 📌 É isso que torna a guarda textual possível: ela varre `onClick={x}` na
   * barra e procura `x` aqui. Um nome próprio obrigaria a uma tabela de tradução, que é mais
   * um sítio para divergir.
   */
  readonly chave: ChaveDoVerbo;
  readonly rotulo: string;
  readonly grupo: GrupoDoVerbo;
  /** O que o gesto faz — a mesma frase que o `title` do botão da barra diz. */
  readonly detalhe: string;
  readonly aplicavel: boolean;
  /**
   * Porque é que não se aplica, **e o que o desbloquearia** (§11 · I).
   *
   * ⚠️ `null` quando se aplica. Uma frase de recusa guardada num verbo aplicável é uma frase
   * que um dia aparece no sítio errado.
   */
  readonly porque: string | null;
}

/**
 * ⚠️⚠️ **Os rótulos aqui NÃO são os da barra, e é deliberado.**
 *
 * 📌 A barra distingue «Viatura e condutor…» pelo **grupo** em que o botão está — há **três**
 * botões com esse rótulo (a viagem que chega, a que parte, e o desactivado do subcontratado).
 * Numa lista plana e pesquisável, três linhas iguais são F-20 com outra roupa: quem escreve
 * «condutor» vê três e não sabe qual escolhe. Cada rótulo daqui tem de se ler **sozinho**.
 */
const ROTULO_DA_INDISPONIBILIDADE = {
  marcar: 'Marcar oficina, avaria ou sinistro…',
  levantar: 'Alterar ou levantar a indisponibilidade…',
} as const;

/** Um verbo antes de se saber se se aplica — a parte que não depende do contexto. */
interface Declaracao {
  readonly chave: ChaveDoVerbo;
  readonly rotulo: string;
  readonly grupo: GrupoDoVerbo;
  readonly detalhe: string;
}

/**
 * Os 22 verbos, pela ordem dos grupos da barra.
 *
 * ⚠️ A ordem importa duas vezes: é a ordem em que aparecem na paleta dentro de cada metade
 * (aplicáveis · não aplicáveis) e é a ordem que o planeador já conhece da barra. Duas ordens
 * para a mesma lista obrigam a reaprender.
 */
const DECLARADOS: readonly Declaracao[] = [
  {
    chave: 'substituir',
    rotulo: 'Quem substitui?',
    grupo: 'urgência',
    detalhe: 'a viatura está indisponível — que serviços ficam por cobrir',
  },
  {
    chave: 'editar',
    rotulo: 'Viatura e condutor — esta viagem…',
    grupo: 'a viagem',
    detalhe: 'quem conduz e que autocarro faz a corrida inteira, não só este dia',
  },
  // ---- 🔴 O PAR DA CÓPIA, e vem LOGO A SEGUIR ao «editar» de propósito ------------------
  //
  // ⚠️⚠️ **É ao lado do gesto que MOVE que a diferença se aprende.** «Viatura e condutor — esta
  //    viagem…» tira a viagem de um autocarro; «Acrescentar um segundo autocarro…» não tira
  //    nada. 📌 Numa lista plana e pesquisável, pô-los longe um do outro deixava a distinção
  //    por descobrir — e ler mal um destes dois deixa um grupo a mais ou a menos num autocarro.
  {
    chave: 'copiarPeriodo',
    rotulo: 'Acrescentar um segundo autocarro à viagem…',
    grupo: 'a viagem',
    detalhe: 'a MESMA viagem passa a correr em dois — o actual fica onde está',
  },
  {
    chave: 'retirarCopia',
    rotulo: 'Tirar este autocarro da viagem…',
    grupo: 'a viagem',
    detalhe: 'apaga só as alocações deste autocarro — a viagem continua no outro',
  },
  {
    chave: 'fecharServico',
    rotulo: 'Fechar o serviço — registar o realizado…',
    grupo: 'a viagem',
    detalhe: 'km, custo e imprevistos do que aconteceu de facto',
  },
  {
    chave: 'retirarDoMapa',
    rotulo: 'Retirar do mapa…',
    grupo: 'a viagem',
    detalhe: 'liberta os dias sem cancelar o pedido — ele volta à fila por planear',
  },
  {
    chave: 'cancelarServico',
    rotulo: 'Cancelar o serviço…',
    grupo: 'a viagem',
    detalhe: 'cancela o pedido e, se quiser, liberta os dias — pede o motivo',
  },
  {
    chave: 'travar',
    rotulo: 'Não mexer — travar esta viagem…',
    grupo: 'a viagem',
    detalhe: 'deixa de entrar no planeamento e nos rearranjos automáticos',
  },
  {
    chave: 'diario',
    rotulo: 'O que aconteceu a esta viagem…',
    grupo: 'a viagem',
    detalhe: 'quem mexeu, quando e o quê — só lê, não altera nada',
  },
  {
    chave: 'editarQuePartem',
    rotulo: 'Viatura e condutor — a viagem que PARTE…',
    grupo: 'a viagem que parte',
    detalhe: 'a SEGUNDA viagem deste dia de rotação, com a referência dela explícita',
  },
  {
    chave: 'corDaViagemQuePartem',
    rotulo: 'Cor da viagem que PARTE…',
    grupo: 'a viagem que parte',
    detalhe: 'uma cor só para a segunda viagem deste dia de rotação',
  },
  {
    chave: 'corrigir',
    rotulo: 'Itinerário e pernoita deste dia…',
    grupo: 'este dia',
    detalhe: 'onde pernoita, o itinerário, as horas e o estado deste dia',
  },
  {
    chave: 'anotar',
    rotulo: 'Nota neste dia…',
    grupo: 'este dia',
    detalhe: 'uma nota breve no dia, e não na viagem toda (fonte 6.8)',
  },
  {
    chave: 'partir',
    rotulo: 'Partir a viagem neste dia…',
    grupo: 'este dia',
    detalhe: 'a segunda metade passa a ser um serviço próprio, com referência nova',
  },
  {
    chave: 'organizarEmTrocos',
    rotulo: 'Organizar a viagem em troços…',
    grupo: 'este dia',
    detalhe: 'troços de N dias para reporte — a referência NÃO muda',
  },
  {
    chave: 'juntarTrocos',
    rotulo: 'Juntar os troços…',
    grupo: 'este dia',
    detalhe: 'apaga as fronteiras de troço — a viagem volta a ser um troço só',
  },
  {
    chave: 'porEmEspera',
    rotulo: 'Pôr a viagem em espera…',
    // ⚠️ Grupo «a viagem» e não «este dia»: o gesto leva a corrida INTEIRA, como o `travar`.
    //    Pô-lo em «este dia» fazia prometer que só tirava o dia que está seleccionado.
    grupo: 'a viagem',
    detalhe: 'sai do autocarro e fica numa linha própria no fim do mapa — nada se perde',
  },
  {
    chave: 'corDaViagem',
    rotulo: 'Cor desta viagem…',
    grupo: 'este dia',
    detalhe: 'uma cor própria em vez da cor do cliente, em todas as células da corrida',
  },
  {
    chave: 'onde',
    rotulo: 'Onde está parada…',
    grupo: 'a viatura',
    detalhe: 'dizer onde a viatura está — na base ou fora, e em que período',
  },
  {
    chave: 'marcarIndisponivel',
    rotulo: ROTULO_DA_INDISPONIBILIDADE.marcar,
    grupo: 'a viatura',
    detalhe: 'marcar em vários dias de uma vez, sem ir célula a célula',
  },
  {
    chave: 'encaixar',
    rotulo: 'O que encaixa aqui?',
    grupo: 'a viatura',
    detalhe: 'que pedidos por planear cabem nesta viatura, nestes dias livres',
  },
  {
    chave: 'trocarEmpresa',
    rotulo: 'Trocar de empresa subcontratada…',
    grupo: 'o serviço subcontratado',
    detalhe: 'a mesma viagem noutra empresa — os mesmos travões e a mesma auditoria',
  },
];

/** As chaves declaradas, para a guarda textual as confrontar com a barra. */
export const CHAVES_DOS_VERBOS: readonly ChaveDoVerbo[] = DECLARADOS.map((d) => d.chave);

/**
 * ⚠️⚠️ **A recusa diz o que a desbloquearia, e não só que não dá** (§11 · I).
 *
 * 📌 *«Isto não se aplica»* manda procurar noutro sítio sem dizer qual. *«Só num dia com
 * serviço»* diz onde carregar a seguir. É a diferença entre uma recusa e um beco, e este
 * projecto já pagou a segunda várias vezes.
 *
 * ⚠️⚠️ **E É CURTA POR MEDIÇÃO, não por preguiça.** A razão vai para a coluna `paleta-detalhe`,
 * que é `white-space: nowrap` com `text-overflow: ellipsis`: na largura da paleta
 * (`min(620px, 92vw)`), descontando a marca (5,5 rem) e o rótulo, sobram ~250 px, que a
 * 11,5 px dão **~45 caracteres**. 📌 Uma frase de 150 caracteres chega ao ecrã cortada no meio
 * — e uma recusa cortada no meio é pior do que nenhuma, porque parece que o sistema tentou
 * explicar-se e falhou. Alargar isto exige uma regra nova no `mapa.css`, declarada no
 * relatório desta passagem.
 */
function porqueNao(c: Declaracao, ctx: ContextoDaSeleccao): string {
  if (ctx.onde === 'subcontratado') {
    if (c.chave === 'editar') return 'o condutor é da empresa';
    return 'a frota é da empresa subcontratada';
  }
  switch (c.chave) {
    case 'substituir':
      return 'só com a viatura indisponível neste dia';
    case 'fecharServico':
      return ctx.natureza === 'servico'
        ? 'só depois de o dia passar'
        : 'só num dia com serviço';
    case 'juntarTrocos':
      return ctx.natureza === 'servico'
        ? 'primeiro «Organizar a viagem em troços…»'
        : 'só num dia com serviço';
    case 'corDaViagem':
    // 🔴 §6.5: as duas rotas exigem a `referencia` e recusam com 400 `alvo_incompleto` sem ela
    //    — é ela que diz QUAL viagem se copia, quando o intervalo tem mais do que uma.
    case 'copiarPeriodo':
      return ctx.natureza === 'servico'
        ? 'este dia não tem referência de viagem'
        : 'só num dia com serviço';
    case 'retirarCopia':
      if (ctx.natureza !== 'servico') return 'só num dia com serviço';
      if (!ctx.temCorrida) return 'este dia não tem referência de viagem';
      // ⚠️ §11 · I — a recusa nomeia o gesto que a levanta, como a de «Juntar os troços…». E é
      //    o espelho do 409 `unica_viatura_com_a_viagem`: sem segundo autocarro não há cópia.
      return 'primeiro «Acrescentar um 2.º autocarro…»';
    case 'editarQuePartem':
    case 'corDaViagemQuePartem':
      return ctx.natureza === 'servico'
        ? 'só num dia de ROTAÇÃO — aqui há uma viagem só'
        : 'só num dia de rotação';
    case 'onde':
      return ctx.natureza === 'servico'
        ? 'num dia de serviço é o itinerário que o diz'
        : 'num dia de oficina é a oficina que o diz';
    case 'encaixar':
      return 'só num dia livre';
    case 'trocarEmpresa':
      return 'só num serviço subcontratado';
    default:
      // Os restantes são todos «só num dia com serviço»: é a condição do grupo na barra.
      return ctx.natureza === 'indisponivel'
        ? 'este dia está em oficina, avaria ou sinistro'
        : 'só num dia com serviço';
  }
}

/**
 * A etiqueta da coluna da esquerda — **o âmbito**, no vocabulário que a barra já usa.
 *
 * ⚠️ `.paleta-marca` tem `min-width: 5.5rem` e **não** tem `ellipsis`: uma etiqueta longa
 * empurra o rótulo em vez de se cortar. Daí «que parte» e «o subcontrato» em vez dos nomes
 * inteiros dos grupos.
 */
const MARCA_DO_GRUPO: Record<GrupoDoVerbo, string> = {
  urgência: 'urgência',
  'a viagem': 'a viagem',
  'a viagem que parte': 'que parte',
  'este dia': 'este dia',
  'a viatura': 'a viatura',
  'o serviço subcontratado': 'o subcontrato',
};

/** A marca de um verbo que não se aplica. ⚠️ Nunca a do grupo: a coluna tem de o denunciar. */
export const MARCA_NAO_DA = 'não dá';

/** A marca da linha, que é a primeira coisa que se lê. */
export function marcaDoVerbo(v: VerboDaSeleccao): string {
  return v.aplicavel ? MARCA_DO_GRUPO[v.grupo] : MARCA_NAO_DA;
}

/** Aplica-se? — o **mesmo** predicado que a barra de acções usa no seu JSX. */
function aplicaSe(c: Declaracao, ctx: ContextoDaSeleccao): boolean {
  if (ctx.onde === 'nada') return false;
  if (ctx.onde === 'subcontratado') {
    // ⚠️ Os três que agem sobre o PEDIDO — e um pedido não sabe nem precisa de saber quem o
    //    executa. O resto é da frota da outra empresa, e não é nosso para mexer (D-002).
    return c.chave === 'cancelarServico' || c.chave === 'retirarDoMapa' || c.chave === 'trocarEmpresa';
  }
  const servico = ctx.natureza === 'servico';
  switch (c.chave) {
    case 'substituir':
      return ctx.indisponivel;
    case 'editar':
    case 'retirarDoMapa':
    case 'cancelarServico':
    case 'travar':
    case 'corrigir':
    case 'anotar':
    case 'partir':
    case 'organizarEmTrocos':
    // 🔴 Só num dia de SERVIÇO: num dia livre ou de oficina não há viagem para tirar do
    //    autocarro, e o servidor recusaria com `servico_nao_encontrado`.
    case 'porEmEspera':
      return servico;
    case 'fecharServico':
      return servico && ctx.jaPassou;
    case 'diario':
      return servico && ctx.temDiario;
    case 'editarQuePartem':
    case 'corDaViagemQuePartem':
      return servico && ctx.temViagemQueParte;
    case 'juntarTrocos':
      return servico && ctx.temFronteiraDeTroco;
    case 'corDaViagem':
    // 🔴 §6.5 · a referência é obrigatória nas duas rotas (400 `alvo_incompleto` sem ela).
    case 'copiarPeriodo':
      return servico && ctx.temCorrida;
    // 🔴 §6.5 · **e mais: a viagem tem de existir noutro autocarro.** Sem isso o que se apagava
    //    era a própria viagem, e o servidor recusa com 409 `unica_viatura_com_a_viagem`.
    case 'retirarCopia':
      return servico && ctx.temCorrida && ctx.temOutraViatura;
    case 'onde':
      return ctx.diaSemItinerario;
    case 'marcarIndisponivel':
      // ⚠️ **SEMPRE**, inclusive em dias livres — é precisamente aí que faltava poder
      //    sinalizar a oficina, e é o que a barra faz.
      return true;
    case 'encaixar':
      return ctx.natureza === 'livre';
    case 'trocarEmpresa':
      return false;
    default:
      return false;
  }
}

/**
 * Os verbos desta selecção — **os aplicáveis primeiro**, os outros a seguir com a razão.
 *
 * ⚠️⚠️ **SEM SELECÇÃO devolve LISTA VAZIA, e isso não é «esconder» (P-30).** 📌 P-30 protege a
 * funcionalidade de parecer inexistente; sem célula escolhida não há sujeito nenhum de que a
 * pergunta se faça, e vinte e duas linhas apagadas a dizer «escolha um dia» empurrariam para fora
 * do ecrã os nove comandos globais — que são exactamente o que faz sentido sem selecção.
 *
 * ⚠️ **Os não aplicáveis FICAM na lista** (é a outra metade de P-30, e é a razão desta peça):
 * um verbo ausente lê-se como *«esta funcionalidade não existe»*, e hoje, num dia livre, são
 * dezanove a desaparecer sem uma palavra.
 */
export function verbosDaSeleccao(ctx: ContextoDaSeleccao): readonly VerboDaSeleccao[] {
  if (ctx.onde === 'nada') return [];
  const avaliados = DECLARADOS.map((d): VerboDaSeleccao => {
    const aplicavel = aplicaSe(d, ctx);
    const rotulo =
      d.chave === 'marcarIndisponivel' && ctx.natureza === 'indisponivel'
        ? ROTULO_DA_INDISPONIBILIDADE.levantar
        : d.rotulo;
    return {
      chave: d.chave,
      rotulo,
      grupo: d.grupo,
      detalhe: d.detalhe,
      aplicavel,
      porque: aplicavel ? null : porqueNao(d, ctx),
    };
  });
  // ⚠️ `filter` duas vezes e não um `sort`: um `sort` com comparador booleano não garante
  //    estabilidade em toda a parte, e a ordem dentro de cada metade é a da barra — que é o que
  //    dispensa reaprender onde as coisas estão.
  return [...avaliados.filter((v) => v.aplicavel), ...avaliados.filter((v) => !v.aplicavel)];
}
